/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'
import { defineMessages, FormattedMessage } from 'react-intl'

import {
  EuiBadge,
  EuiButton,
  EuiButtonEmpty,
  EuiContextMenuItem,
  EuiRadioGroup,
  EuiLoadingSpinner,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiOverlayMask,
  EuiText,
  EuiTitle,
  htmlIdGenerator,
} from '@elastic/eui'

import type { AsyncRequestState, StackDeployment, RestartStrategy } from '@modules/ui-types'
import { parseError } from '@modules/cui/Alert'
import { addToast } from '@modules/cui/Toasts'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

import { getMaxAvailableZonesForDeployment } from '@/lib/deployments/availabilityZones'

type StateProps = {
  restartStackDeploymentEsResourceRequest: AsyncRequestState
}

type DispatchProps = {
  restartStackDeploymentEsResource: (strategy: RestartStrategy) => void
  resetRestartStackDeploymentEsResource: () => void
  fetchDeployment: () => void
}

type ConsumerProps = {
  deployment: StackDeployment
  onRestart?: () => void
}

type Props = StateProps & DispatchProps & ConsumerProps

type State = {
  idPrefix: string
  selectedStrategy: RestartStrategy
  requestChainProgress: boolean
  isConfirmModalOpen: boolean
}

const makeId = htmlIdGenerator()

const messages = defineMessages({
  fullRestart: {
    id: `deployment.force-restart.strategy.fullRestart`,
    defaultMessage: `Full restart. All instances restart simultaneously.`,
  },
  zoneByZone: {
    id: `deployment.force-restart.strategy.zoneByZone`,
    defaultMessage: `Restart all instances within an availability zone, before moving on to the next zone.`,
  },
  rolling: {
    id: `deployment.force-restart.strategy.rolling`,
    defaultMessage: `Restart instances one at a time. {recommended}`,
  },
  recommended: {
    id: `deployment.force-restart.strategy.recommended`,
    defaultMessage: `Recommended`,
  },
  preamble: {
    id: `deployment.force-restart.preamble`,
    defaultMessage: `How do you want to apply the restart?`,
  },
  confirm: {
    id: `deployment.force-restart.confirm`,
    defaultMessage: `Restart Elasticsearch`,
  },
  title: {
    id: `deployment.force-restart.title`,
    defaultMessage: `Restart Elasticsearch?`,
  },
  buttonText: {
    id: `deployment.force-restart.buttonText`,
    defaultMessage: `Restart Elasticsearch`,
  },
  noDowntime: {
    id: `deployment.force-restart.no-downtime`,
    defaultMessage: `No downtime`,
  },
  withDowntime: {
    id: `deployment.force-restart.with-downtime`,
    defaultMessage: `With downtime`,
  },
})

class RestartDeployment extends Component<Props, State> {
  mounted: boolean = false

  state: State = {
    idPrefix: makeId(),
    selectedStrategy: `__name__`,
    requestChainProgress: false,
    isConfirmModalOpen: false,
  }

  componentDidMount() {
    this.mounted = true
  }

  componentWillUnmount() {
    this.props.resetRestartStackDeploymentEsResource()
    this.mounted = false
  }

  render() {
    const {
      restartStackDeploymentEsResourceRequest: { inProgress },
      deployment,
    } = this.props

    const { requestChainProgress } = this.state

    const isBusy = requestChainProgress || inProgress
    return (
      <Fragment>
        <PermissionsGate
          permissions={[
            {
              type: 'deployment',
              action: 'restart',
              id: deployment.id,
            },
          ]}
        >
          {({ hasPermissions }) => (
            <EuiContextMenuItem
              icon={isBusy ? <EuiLoadingSpinner size='m' /> : 'refresh'}
              onClick={this.openConfirmModal}
              data-test-id='forceRestartDeployment-btn'
              disabled={isBusy || !hasPermissions}
            >
              <FormattedMessage {...messages.buttonText} />
            </EuiContextMenuItem>
          )}
        </PermissionsGate>

        {this.renderConfirmModal()}
      </Fragment>
    )
  }

  renderConfirmModal() {
    const { isConfirmModalOpen, idPrefix, selectedStrategy } = this.state
    const { deployment } = this.props
    const downtimeOptions = [
      {
        id: `${idPrefix}__all__`,
        label: <FormattedMessage {...messages.fullRestart} />,
        value: `__all__`,
      },
    ]

    const noDowntimeOptions = [
      {
        id: `${idPrefix}__name__`,
        label: (
          <FormattedMessage
            {...messages.rolling}
            values={{
              recommended: (
                <EuiBadge color='success'>
                  <FormattedMessage {...messages.recommended} />
                </EuiBadge>
              ),
            }}
          />
        ),
        value: `__name__`,
      },
      {
        id: `${idPrefix}__zone__`,
        label: <FormattedMessage {...messages.zoneByZone} />,
        value: `__zone__`,
        disabled: getMaxAvailableZonesForDeployment(deployment) < 2,
      },
    ]

    if (!isConfirmModalOpen) {
      return null
    }

    return (
      <EuiOverlayMask>
        <EuiModal onClose={this.closeConfirmModal}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <FormattedMessage {...messages.title} />
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            <EuiText>
              <div data-test-id='forceRestartDeployment-modal'>
                <p>
                  <FormattedMessage {...messages.preamble} />
                </p>

                <EuiTitle size='xs'>
                  <h5>
                    <FormattedMessage {...messages.noDowntime} />
                  </h5>
                </EuiTitle>

                <EuiRadioGroup
                  data-test-id='forceRestartDeployment-no-downtime-options'
                  options={noDowntimeOptions}
                  idSelected={`${idPrefix}${selectedStrategy}`}
                  onChange={this.onChange}
                />

                <EuiTitle size='xs'>
                  <h5>
                    <FormattedMessage {...messages.withDowntime} />
                  </h5>
                </EuiTitle>

                <EuiRadioGroup
                  data-test-id='forceRestartDeployment-downtime-options'
                  options={downtimeOptions}
                  idSelected={`${idPrefix}${selectedStrategy}`}
                  onChange={this.onChange}
                />
              </div>
            </EuiText>
          </EuiModalBody>
          <EuiModalFooter>
            <EuiButtonEmpty onClick={this.closeConfirmModal} data-test-id='delete-text-cancel'>
              <FormattedMessage id='deployment-delete-deployment.cancel' defaultMessage='Cancel' />
            </EuiButtonEmpty>

            <EuiButton onClick={this.restart} data-test-id='confirmModalConfirmButton'>
              <FormattedMessage {...messages.confirm} />
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      </EuiOverlayMask>
    )
  }

  closeConfirmModal = () => {
    this.setState({ isConfirmModalOpen: false })
  }

  openConfirmModal = () => {
    this.setState({ isConfirmModalOpen: true })
  }

  onChange = (_id: string, value: RestartStrategy) => {
    this.setState({ selectedStrategy: value })
  }

  restart = () => {
    const { restartStackDeploymentEsResource, fetchDeployment, onRestart } = this.props
    const { selectedStrategy } = this.state

    if (onRestart) {
      onRestart()
    }

    Promise.resolve()
      .then(() => this.setState({ requestChainProgress: true, isConfirmModalOpen: false }))
      .then(() => restartStackDeploymentEsResource(selectedStrategy))
      .then(() => {
        if (!onRestart) {
          return fetchDeployment()
        }

        return null
      })
      .then(() => {
        if (this.mounted) {
          return this.setState({ requestChainProgress: false })
        }

        return null
      })
      .catch((error) => {
        if (this.mounted) {
          addToast({
            family: `toast-error`,
            color: `danger`,
            iconType: `broom`,
            title: (
              <FormattedMessage
                id='toasts.title-failed'
                defaultMessage='Restarting deployment failed'
              />
            ),
            text: parseError(error),
          })

          return this.setState({ requestChainProgress: false })
        }

        return null
      })
  }
}

export default RestartDeployment
